import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, ChevronRight, ChevronLeft, X, CheckCircle, Sparkles } from 'lucide-react';

// User provided illustrations for Wudu
const wuduImages = {
  nijet: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/2wiv6tkx_IMG_0371.jpeg",
  duar: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/rt2mgcfe_IMG_0366.jpeg",
  goje: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/4qkfw6fh_IMG_0367.jpeg",
  hunde: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/4qkfw6fh_IMG_0367.jpeg",
  fytyre: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/cyyjxqls_IMG_0370.jpeg",
  krahe: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/0k7agdej_IMG_0369.jpeg",
  koke: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/5l05yu1v_IMG_0368.jpeg",
  kembe: "https://customer-assets.emergentagent.com/job_mosque-portal-8/artifacts/ablfk88t_IMG_0372.jpeg",
};

const wuduSteps = [
  {
    step: 1,
    title: "Nijeti (Qëllimi)",
    arabic: "النِّيَّة",
    description: "Bëj nijet në zemër për të marrë abdes për namaz. Thuaj: 'Bismilah' (Në emër të Allahut).",
    image: wuduImages.nijet,
    tip: "Nijeti është kusht i domosdoshëm për abdesin",
    color: "from-emerald-500 to-teal-600"
  },
  {
    step: 2,
    title: "Larja e Duarve",
    arabic: "غَسْلُ الْيَدَيْنِ",
    description: "Laj duart tre herë deri në kyçe. Fillo me dorën e djathtë, pastaj të majtën. Sigurohu që uji të arrijë mes gishtave.",
    image: wuduImages.duar,
    tip: "Laji gishtërinjtë dhe pjesët mes tyre mirë",
    color: "from-blue-500 to-cyan-600"
  },
  {
    step: 3,
    title: "Shpëlarja e Gojës",
    arabic: "الْمَضْمَضَة",
    description: "Merr ujë në gojë tre herë dhe shpëlaje mirë. Përdor dorën e djathtë për të marrë ujë.",
    image: wuduImages.goje,
    tip: "Nëse nuk je agjërueshëm, mund ta përdorësh misvakun",
    color: "from-violet-500 to-purple-600"
  },
  {
    step: 4,
    title: "Shpëlarja e Hundës",
    arabic: "الاِسْتِنْشَاق",
    description: "Tërheq ujë në hundë tre herë me dorën e djathtë dhe pastro me dorën e majtë. Bëje butësisht.",
    image: wuduImages.hunde,
    tip: "Mos e teproni nëse jeni agjërueshëm",
    color: "from-pink-500 to-rose-600"
  },
  {
    step: 5,
    title: "Larja e Fytyrës",
    arabic: "غَسْلُ الْوَجْهِ",
    description: "Laj fytyrën tre herë nga linja e flokëve deri tek mjekra, dhe nga veshi deri tek veshi tjetër.",
    image: wuduImages.fytyre,
    tip: "Sigurohu që uji të arrijë në çdo pjesë të fytyrës",
    color: "from-amber-500 to-orange-600"
  },
  {
    step: 6,
    title: "Larja e Krahëve",
    arabic: "غَسْلُ الْيَدَيْنِ إِلَى الْمِرْفَقَيْنِ",
    description: "Laj krahun e djathtë tre herë nga gishtërinjtë deri tek bërryli (përfshirë bërrylin). Pastaj bëj të njëjtën me krahun e majtë.",
    image: wuduImages.krahe,
    tip: "Mos harro bërrylin - është farz",
    color: "from-green-500 to-emerald-600"
  },
  {
    step: 7,
    title: "Mesh'hi i Kokës",
    arabic: "مَسْحُ الرَّأْسِ",
    description: "Lago duart dhe fshij kokën një herë duke filluar nga përpara drejt mbrapa, pastaj kthehu. Fshij veshët me gishtat.",
    image: wuduImages.koke,
    tip: "Mesh'hi i kokës bëhet vetëm një herë",
    color: "from-indigo-500 to-blue-600"
  },
  {
    step: 8,
    title: "Larja e Këmbëve",
    arabic: "غَسْلُ الرِّجْلَيْنِ",
    description: "Laj këmbën e djathtë tre herë deri tek nyja (përfshirë nyjen). Pastaj bëj të njëjtën me këmbën e majtë. Laj mirë mes gishtave.",
    image: wuduImages.kembe,
    tip: "Gishtërinjtë dhe nyjet - mos i harro!",
    color: "from-teal-500 to-cyan-600"
  }
];

const WuduTutorial = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [direction, setDirection] = useState(1);

  const handleNext = () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }
    if (currentStep < wuduSteps.length - 1) {
      setDirection(1);
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (index) => {
    setDirection(index > currentStep ? 1 : -1);
    setCurrentStep(index);
  };

  const handleClose = () => {
    setCurrentStep(0);
    setCompletedSteps([]);
    onClose();
  };

  if (!isOpen) return null;

  const step = wuduSteps[currentStep];
  const progress = ((currentStep + 1) / wuduSteps.length) * 100;

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0
    })
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-2 sm:p-4"
        onClick={handleClose}
        data-testid="wudu-tutorial-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 30 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="bg-white rounded-2xl sm:rounded-3xl w-full max-w-3xl max-h-[95vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="wudu-tutorial-modal"
        >
          {/* Header - Compact on mobile */}
          <div className={`relative bg-gradient-to-r ${step.color} p-3 sm:p-5 text-white`}>
            {/* Progress bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-white/20">
              <motion.div 
                className="h-full bg-white"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Droplets className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-heading font-bold">Si të Marrim Abdes</h2>
                  <p className="text-white/80 text-xs sm:text-sm flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Hapi {step.step} nga {wuduSteps.length}
                  </p>
                </div>
              </div>
              <motion.button
                onClick={handleClose}
                className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30"
                whileTap={{ scale: 0.9 }}
                data-testid="wudu-close-btn"
              >
                <X className="w-5 h-5" />
              </motion.button>
            </div>

            {/* Step indicators - Scrollable on mobile */}
            <div className="flex gap-1.5 sm:gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
              {wuduSteps.map((s, index) => (
                <button
                  key={index}
                  onClick={() => handleStepClick(index)}
                  className={`flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all ${
                    index === currentStep
                      ? 'bg-white text-gray-800 shadow-lg scale-110'
                      : completedSteps.includes(index)
                      ? 'bg-white/40 text-white'
                      : 'bg-white/20 text-white/80'
                  }`}
                  data-testid={`wudu-step-${index + 1}`}
                >
                  {completedSteps.includes(index) ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    index + 1
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content - Stack on mobile, side by side on desktop */}
          <div className="p-3 sm:p-5 overflow-y-auto max-h-[calc(95vh-220px)]">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentStep}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                className="space-y-4"
              >
                {/* Image - Full width on mobile */}
                <div className="relative rounded-xl sm:rounded-2xl overflow-hidden">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full h-48 sm:h-64 object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${step.color} opacity-20`} />
                  <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4">
                    <span className="bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-semibold">
                      Hapi {step.step}: {step.title}
                    </span>
                  </div>
                </div>

                {/* Text Content */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl sm:text-2xl font-heading font-bold text-gray-800">
                      {step.title}
                    </h3>
                    <span className="text-lg sm:text-xl text-gray-400" style={{ fontFamily: 'Amiri, serif' }}>
                      {step.arabic}
                    </span>
                  </div>
                  
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4">
                    {step.description}
                  </p>
                  
                  {/* Tip Box */}
                  <div className={`bg-gradient-to-r ${step.color} bg-opacity-10 rounded-xl p-3 sm:p-4`}>
                    <div className="flex items-start gap-2">
                      <span className="text-xl">💡</span>
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">Këshillë</p>
                        <p className="text-gray-600 text-sm">{step.tip}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation - Fixed at bottom */}
          <div className="p-3 sm:p-4 bg-gray-50 border-t flex items-center justify-between gap-2">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-1 px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base transition-all ${
                currentStep === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border-2 border-gray-200 active:scale-95'
              }`}
              data-testid="wudu-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Mbrapa</span>
            </button>

            <span className="text-xs sm:text-sm text-gray-500 hidden sm:block">
              {completedSteps.length}/{wuduSteps.length}
            </span>

            <button
              onClick={handleNext}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-1 px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base transition-all bg-gradient-to-r ${step.color} text-white shadow-lg active:scale-95`}
              data-testid="wudu-next-btn"
            >
              {currentStep === wuduSteps.length - 1 ? (
                <>
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Përfundo</span>
                </>
              ) : (
                <>
                  <span>Vazhdo</span>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WuduTutorial;
